import { useEffect, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import type { Language } from '@/types';
import { fetchJobPhotos, mediaUrl, type JobPhoto } from '../api';

interface QCPhotoGridProps {
  jobId: string;
  token: string | null;
  language: Language;
  blockId?: string;
}

export function QCPhotoGrid({ jobId, token, language, blockId }: QCPhotoGridProps) {
  const [photos, setPhotos] = useState<JobPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!token) {
      setPhotos([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchJobPhotos(token, jobId)
      .then((res) => {
        if (cancelled) return;
        setPhotos(blockId ? res.filter((p) => p.job_block_id === blockId) : res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load photos');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, token, blockId]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 font-mono text-xs text-[#607C6E]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {language === 'sv' ? 'Laddar bilder…' : 'Loading photos…'}
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-3 font-mono text-xs text-[#C24138]">{error}</div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="flex items-center gap-2 py-3 font-mono text-xs text-[#607C6E]">
        <ImageOff className="h-3.5 w-3.5" />
        {language === 'sv' ? 'Inga bilder' : 'No photos'}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {photos.map((photo) => {
        const broken = brokenIds.has(photo.id);
        return (
          <a
            key={photo.id}
            href={mediaUrl(photo.storage_path)}
            target="_blank"
            rel="noreferrer"
            className="block overflow-hidden rounded border border-[#073331]/[0.12] bg-[#073331]/[0.04] transition-colors hover:border-[#8A6A47]"
          >
            {broken ? (
              <div className="flex h-28 items-center justify-center text-[#607C6E]">
                <ImageOff className="h-5 w-5" />
              </div>
            ) : (
              <img
                src={mediaUrl(photo.storage_path)}
                alt=""
                loading="lazy"
                className="h-28 w-full object-cover"
                onError={() =>
                  setBrokenIds((prev) => {
                    const next = new Set(prev);
                    next.add(photo.id);
                    return next;
                  })
                }
              />
            )}
          </a>
        );
      })}
    </div>
  );
}
